import { ChevronRight } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import ExportButtons from '../../../components/ExportButtons'
import SearchBar from '../../../components/SearchBar'
import type { ColumnDefinition, ExportMetadata } from '../../../utils/exportToCsv'
import type { ModellRow } from './api'
import { formatAndel } from './api'

interface Props {
  rows: ModellRow[]
  selectedMerke?: string | null
  onRowClick?: (row: ModellRow) => void
  /** Opens the (merke, modell) market-share page (#489 AC6). Callback rather
   *  than a router hook — see MerkeTable. Omitted = no Detaljer column. */
  onDetaljer?: (row: ModellRow) => void
  /** Period / active-filter context for the export; merged over the table's
   *  own default title. */
  exportMeta?: Partial<ExportMetadata>
  // Pagination + server-side search (#476) — all optional; when omitted the
  // table renders exactly as before (no search box, no footer, Rang = row
  // index + 1). See MerkeTable.tsx for the same pattern's rationale.
  page?: number
  pages?: number
  total?: number
  onPageChange?: (page: number) => void
  search?: string
  onSearchChange?: (q: string) => void
  startRank?: number
}

export default function ModellTable({
  rows, selectedMerke, onRowClick, onDetaljer, exportMeta,
  page, pages, total, onPageChange, search, onSearchChange, startRank = 1,
}: Props) {
  const { t } = useTranslation('bruktbil')
  const exportColumns: ColumnDefinition[] = [
    { key: 'rang', label: t('common.rang') },
    { key: 'merke', label: t('common.merke') },
    { key: 'modell', label: t('common.modell') },
    { key: 'count', label: t('common.antall'), format: (v: number) => v.toLocaleString('nb-NO') },
    { key: 'andel', label: t('common.markedsandel'), format: (v: number) => formatAndel(v) },
  ]

  // The export rows mirror the rendered table, including the Rang column.
  const exportRows = rows.map((row, i) => ({ rang: startRank + i, ...row }))

  return (
    <div data-testid="modell-table" className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {selectedMerke ? (
            <>
              <Trans
                t={t}
                i18nKey="merkeModell.modellTable.titleFor"
                values={{ merke: selectedMerke }}
                components={{ m: <span className="font-medium" /> }}
              />
            </>
          ) : (
            t('merkeModell.modellTable.title')
          )}
        </span>
        <ExportButtons
          data={exportRows}
          columns={exportColumns}
          metadata={{ title: t('merkeModell.modellTable.exportTitle'), ...exportMeta }}
        />
      </div>
      {onSearchChange && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border">
          <SearchBar value={search ?? ''} onChange={onSearchChange} placeholder={t('merkeModell.modellTable.sok')} />
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.rang')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.merke')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.modell')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.markedsandel')}</th>
            {onDetaljer && <th className="px-4 py-2 text-center font-medium text-gray-600 dark:text-gray-400"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={`${row.merke ?? ''}-${row.modell ?? ''}-${i}`}
              onClick={() => onRowClick?.(row)}
              className={`border-b border-gray-100 dark:border-dark-border ${
                onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-border' : ''
              }`}
            >
              <td className="px-4 py-2 text-gray-500 tabular-nums">{startRank + i}</td>
              <td className="px-4 py-2">{row.merke ?? '—'}</td>
              <td className="px-4 py-2">{row.modell ?? '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.count.toLocaleString('nb-NO')}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatAndel(row.andel)}</td>
              {/* Both names go in the URL: SVV modell strings collide across
               *  brands, so a modell-only link would land on a mix (#489 AC6). */}
              {onDetaljer && <td className="px-4 py-2 text-center">
                {row.merke && row.modell && (
                  <button
                    type="button"
                    data-testid={`modell-detaljer-${row.merke}-${row.modell}`}
                    onClick={e => { e.stopPropagation(); onDetaljer(row) }}
                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <span className="text-xs font-medium">{t('common.seDetaljer')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('common.ingenTreff')}</p>
      )}

      {onPageChange && (pages ?? 1) > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 px-4 py-3 border-t border-gray-200 dark:border-dark-border">
          <span>
            {t('pagination.sideAvTotalt', { page, pages, total: (total ?? 0).toLocaleString('nb-NO') })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, (page ?? 1) - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('pagination.forrige')}
            </button>
            <button
              onClick={() => onPageChange(Math.min(pages ?? 1, (page ?? 1) + 1))}
              disabled={page === pages}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('pagination.neste')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
